import type { GrowthBook, TrackingCallback } from "@growthbook/growthbook-react";

import { trackEvent } from "@/core/analytics/telemetry/telemetry";
import { createGrowthBookClient } from "@/core/featureFlags/growthbook.client";
import { VISITOR_ID_ATTRIBUTE } from "@/core/featureFlags/growthbook.config";
import type { FeatureFlagAttributes } from "@/core/featureFlags/types";
import { getOrCreateVisitorId } from "@/core/identity/visitorId";

/**
 * The page's ONE GrowthBook client, held at module level so it has a
 * lifecycle outside React: the instrumentation bus's growthbook sink starts
 * and stops it, and `FeatureFlagProvider` reads it from here instead of
 * constructing its own. Two clients would bucket the same visitor twice, so
 * this slot — not React state — is the identity.
 *
 * Created on first read rather than only on `start`: the provider renders
 * (and consumers evaluate flags) before any effect runs, so the client must
 * exist by the time the first render asks for it. `start` then finds it
 * already there. It is created carrying the visitor id, so the first
 * evaluation already has the attribute experiments hash on.
 *
 * BROWSER ONLY. Module state on a server is process state shared by every
 * request that process serves — a client held here would carry one
 * request's attributes and features into the next. So on the server this
 * slot is never populated: reads return `null`, and the provider renders the
 * server pass with a throwaway per-request client instead.
 *
 * `null` after `stop` until something reads it again, which creates a fresh
 * client — a destroyed GrowthBook is terminal (its subscriptions and payload
 * are gone), so re-starting means re-creating. Readers subscribed through
 * `subscribeToGrowthBookClient` are told, so React follows the new instance.
 */
let client: GrowthBook<FeatureFlagAttributes> | null = null;

const isServer = (): boolean => typeof window === "undefined";

const listeners = new Set<() => void>();

const notify = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

/**
 * Tags where an exposure came from: the dev Segment source (and its warehouse)
 * is shared by dev and every snap, so exposures must be filterable by origin.
 */
const resolveAppEnv = (): string => {
  const snapId = process.env.NEXT_PUBLIC_SNAP_ID;
  if (snapId) {
    return `snap-${snapId}`;
  }
  return process.env.NEXT_PUBLIC_DATADOG_ENV ?? process.env.NODE_ENV;
};

/**
 * Every exposure is published as Segment's `Experiment Viewed`. Consent is the bus's job: it
 * holds an exposure seen before the visitor answers and sends it if they grant analytics.
 */
const reportExposure: TrackingCallback = (experiment, result) => {
  trackEvent("Experiment Viewed", {
    experiment_id: experiment.key,
    variation_id: result.key,
    hash_attribute: result.hashAttribute,
    hash_value: String(result.hashValue),
    app_env: resolveAppEnv(),
  });
};

/** The page's client — or `null` on the server, where nothing is ever held. */
export const getOrCreateGrowthBookClient = (): GrowthBook<FeatureFlagAttributes> | null => {
  if (isServer()) {
    return null;
  }
  if (client === null) {
    const visitorId = getOrCreateVisitorId();
    if (visitorId === null) {
      throw new Error("No visitor id in the browser, so experiments have nothing to hash on");
    }
    client = createGrowthBookClient({
      attributes: { [VISITOR_ID_ATTRIBUTE]: visitorId },
      trackingCallback: reportExposure,
    });
  }
  return client;
};

/** Ensure the client exists. What the growthbook sink's `activate` means. */
export const startGrowthBookClient = (): void => {
  getOrCreateGrowthBookClient();
};

/**
 * Destroy the client and release the slot. What the growthbook sink's
 * `deactivate` means; subscribers re-read and get a fresh instance.
 */
export const stopGrowthBookClient = (): void => {
  if (client === null) {
    return;
  }

  const stopping = client;
  client = null;
  stopping.destroy();
  notify();
};

/** `useSyncExternalStore`-shaped subscription to the slot changing. */
export const subscribeToGrowthBookClient = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Test-only: drop the slot without destroying. */
export const resetGrowthBookRuntimeForTests = (): void => {
  client = null;
  listeners.clear();
};
